import {
  WorklistFilters,
  type FilterDefinitionType,
  type FilterStateType,
  type WorklistFiltersType,
} from './types';

import { SORTABLE_WORKLIST_COLUMN_MAP, SORT_ORDER } from 'config/constants';
import { WorklistSurface } from '../Worklist/types';
import type { WorklistSurfaceType } from '../Worklist/types';
import { WorklistItemStatus, type SortColumn, type SortOrder } from 'generated/graphql';
import { startOfDay } from 'date-fns/startOfDay';
import { subDays } from 'date-fns/subDays';
import { subMinutes } from 'date-fns/subMinutes';
import { endOfDay } from 'date-fns/endOfDay';

export const DEFAULT_WORKLIST_SORT_COLUMN_ID = 'studyDate';
export const DEFAULT_SORT_COLUMNS: Array<SortColumn> = [
  SORTABLE_WORKLIST_COLUMN_MAP[DEFAULT_WORKLIST_SORT_COLUMN_ID] as SortColumn,
];

export const DEFAULT_SORT_ORDERS: Array<SortOrder> = [SORT_ORDER.ASCENDING as SortOrder];

export const DEFAULT_EMPTY_WORKLIST_SURFACE_FILTERS: {
  [key: string]: FilterStateType;
} = Object.fromEntries(
  Object.values(WorklistSurface).map((surface: WorklistSurfaceType) => [surface, {}])
);

export type FilterConfig = Partial<
  Record<
    WorklistSurfaceType,
    {
      lockedFilters: FilterStateType;
      defaultFilters: FilterDefinitionType;
      hiddenFilters: WorklistFiltersType[];
    }
  >
>;

export const WorklistFilterDateTypes = [
  'submittedAt',
  'submitted_at',
  'submitted_at_before',
  'submitted_at_after',
  'studyDate',
  'study_datetime',
  'study_datetime_before',
  'study_datetime_after',
  'patient_dob',
  'patientDob',
  'patient_dob_before',
  'patient_dob_after',
];

// These filters are invalid for custom worklists because they contain
// patient-specific data that should not be saved as persistent filters
// or are filters that we do not currently support as a column
export const INVALID_CUSTOM_WORKLIST_FILTERS: WorklistFiltersType[] = [
  WorklistFilters.AccessionNumber,
  WorklistFilters.Mrn,
  WorklistFilters.PatientName,
  WorklistFilters.PatientDob,
  WorklistFilters.Age,
  WorklistFilters.Views,
  WorklistFilters.QcStatus,
  WorklistFilters.ClaimedBy,
];

export const DEFAULT_QC_STATUSES = [
  WorklistItemStatus.Qc,
  WorklistItemStatus.InTransit,
  WorklistItemStatus.Scheduled,
  WorklistItemStatus.Mismatched,
];

// These are Sirona-made procedure codes that should not be included in the worklist.
export const EXCLUDED_EXAM_CODES = /^(?:MISMATCHED|PRIOR|EMPTY)$/i;

export const RELATIVE_DATE_TYPES = {
  // TODO: Switch to "24 hours ago" once backend supports hours-based relative filtering.
  TODAY: '1440 minutes ago',
  LAST_7_DAYS: '7 days ago',
  LAST_14_DAYS: '14 days ago',
  LAST_30_DAYS: '30 days ago',
} as const;

export type RelativeDateType = (typeof RELATIVE_DATE_TYPES)[keyof typeof RELATIVE_DATE_TYPES];

interface RelativeDateConfig {
  label: string;
  getAbsoluteRange: (today: Date) => [Date, Date];
}

export function makeRelativeDateRange(today: Date, days: number): [Date, Date] {
  return [startOfDay(subDays(today, days)), endOfDay(today)];
}

export function makeRelativeMinutesRange(today: Date, minutes: number): [Date, Date] {
  return [subMinutes(today, minutes), today];
}

export const RELATIVE_DATE_OPTIONS: Record<string, RelativeDateConfig> = {
  [RELATIVE_DATE_TYPES.TODAY]: {
    label: 'Last 24 hours',
    getAbsoluteRange: (today: Date) => makeRelativeMinutesRange(today, 1440),
  },
  [RELATIVE_DATE_TYPES.LAST_7_DAYS]: {
    label: 'Last 7 days',
    getAbsoluteRange: (today: Date) => makeRelativeDateRange(today, 7),
  },
  [RELATIVE_DATE_TYPES.LAST_14_DAYS]: {
    label: 'Last 14 days',
    getAbsoluteRange: (today: Date) => makeRelativeDateRange(today, 14),
  },
  [RELATIVE_DATE_TYPES.LAST_30_DAYS]: {
    label: 'Last 30 days',
    getAbsoluteRange: (today: Date) => makeRelativeDateRange(today, 30),
  },
};

export const RELATIVE_DATE_REGEX = /^\s*(?:(\d+)\s+day(?:s)?|1440\s+minutes?)\s+ago\s*$/i;

export const DEFAULT_MIN_DATE = new Date(1895, 11, 8);
